import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabaseClient";
import PaymentStatusBadge from "@/components/base/PaymentStatusBadge";
import type { Client, ClientPayment } from "../types";

interface Props {
  client: Client;
  tenantId: string;
}

const fmtDate = (d: string | null) => {
  if (!d) return "—";
  // payment_date is a text field (e.g. "2024-05-18") — parse safely
  const parsed = new Date(d);
  if (isNaN(parsed.getTime())) return d;
  return parsed.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const fmtAmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);

/* Confirmed statuses used in this project */
const CONFIRMED_STATUSES = ["confirmed", "paid", "completed"];

export default function PaymentsTab({ client, tenantId }: Props) {
  const [payments, setPayments] = useState<ClientPayment[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from("payments")
      .select("id, amount, status, method, notes, reference, payment_date, contract_id, contract_number, created_at")
      .eq("tenant_id", tenantId)
      .eq("client_id", client.id)
      .order("created_at", { ascending: false });
    setPayments((data as ClientPayment[]) ?? []);
    setLoading(false);
  }, [client.id, tenantId]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  const totalConfirmed = payments
    .filter((p) => CONFIRMED_STATUSES.includes(p.status))
    .reduce((acc, p) => acc + Number(p.amount), 0);

  const totalPending = payments
    .filter((p) => !CONFIRMED_STATUSES.includes(p.status))
    .reduce((acc, p) => acc + Number(p.amount), 0);

  return (
    <div className="space-y-5">
      {/* Summary cards */}
      {!loading && payments.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          {[
            {
              label: "Confirmed",
              value: fmtAmt(totalConfirmed),
              icon: "ri-checkbox-circle-line",
              color: "text-emerald-600",
              bg: "bg-emerald-50",
            },
            {
              label: "Pending",
              value: fmtAmt(totalPending),
              icon: "ri-time-line",
              color: "text-amber-600",
              bg: "bg-amber-50",
            },
            {
              label: "Transactions",
              value: String(payments.length),
              icon: "ri-receipt-line",
              color: "text-[#9a7840]",
              bg: "bg-[#C8A96E]/10",
            },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-xl border border-slate-200 p-4">
              <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${s.bg} mb-2`}>
                <i className={`${s.icon} ${s.color} text-base`}></i>
              </div>
              <p className="text-slate-400 text-[10px] uppercase tracking-wider">{s.label}</p>
              <p className={`text-lg font-bold mt-0.5 ${s.color}`}>{s.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="flex items-center gap-2 text-slate-400 text-sm py-8 justify-center">
          <div className="w-4 h-4 border-2 border-slate-200 border-t-slate-400 rounded-full animate-spin"></div>
          Loading payments…
        </div>
      ) : payments.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
          <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-[#C8A96E]/10">
            <i className="ri-money-dollar-circle-line text-[#9a7840] text-xl"></i>
          </div>
          <p className="text-slate-700 font-medium text-sm">No payments recorded</p>
          <p className="text-slate-400 text-xs">Payments linked to this client will appear here</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="text-left text-slate-400 text-[10px] uppercase tracking-wider px-5 py-3">
                  Description
                </th>
                <th className="text-left text-slate-400 text-[10px] uppercase tracking-wider px-5 py-3">
                  Amount
                </th>
                <th className="text-left text-slate-400 text-[10px] uppercase tracking-wider px-5 py-3">
                  Status
                </th>
                <th className="text-left text-slate-400 text-[10px] uppercase tracking-wider px-5 py-3">
                  Date
                </th>
                <th className="text-left text-slate-400 text-[10px] uppercase tracking-wider px-5 py-3">
                  Reference
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {payments.map((p) => (
                <tr key={p.id} className="hover:bg-slate-50/50 transition-colors">
                  <td className="px-5 py-3.5">
                    <p className="text-slate-700 text-sm font-medium">
                      {p.notes || (p.contract_number ? `Contract ${p.contract_number}` : "Payment")}
                    </p>
                    {p.method && (
                      <p className="text-slate-400 text-xs capitalize mt-0.5">{p.method}</p>
                    )}
                  </td>
                  <td className="px-5 py-3.5">
                    <p className="text-slate-800 font-semibold text-sm">{fmtAmt(Number(p.amount))}</p>
                  </td>
                  <td className="px-5 py-3.5">
                    <PaymentStatusBadge status={p.status} />
                  </td>
                  <td className="px-5 py-3.5">
                    <p className="text-slate-400 text-xs">{fmtDate(p.payment_date)}</p>
                  </td>
                  <td className="px-5 py-3.5">
                    <p className="text-slate-400 text-xs font-mono">
                      {p.reference || "—"}
                    </p>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}